import { Injectable, inject } from '@angular/core';
import { CommunityTabType } from '../../interfaces';
import { FeedFacade } from '../../store';

@Injectable({
  providedIn: 'root',
})
export class ScrollManagementService {
  private static readonly SCROLL_BUFFER = 100;
  private static readonly SCROLL_DELAY = 200;
  private static readonly DEFAULT_TIMER_DELAY = 50;
  private static readonly RESTORE_DELAY = 100;
  private static readonly MAX_RESTORE_ATTEMPTS = 10;
  private static readonly CONTENT_LOAD_DELAY = 150;

  private scrollTimeout: number | null = null;
  private feedFacade = inject(FeedFacade);

  restoreScrollPosition(position: number): void {
    if (position <= 0) return;
    this.attemptScrollRestoreWithRetries(position, 0);
  }

  restoreScrollPositionWithHeight(position: number, savedDocumentHeight: number): void {
    if (position <= 0) return;

    if (!savedDocumentHeight || savedDocumentHeight <= 0) {
      this.restoreScrollPosition(position);
      return;
    }

    const currentHeight = this.getDocumentHeight();
    const heightDifference = currentHeight - savedDocumentHeight;
    const adjustedPosition = Math.max(0, position + heightDifference);
    this.attemptScrollRestoreWithRetries(adjustedPosition, 0);
  }

  restoreScrollAfterLoad(tabId: CommunityTabType): void {
    const checkAndRestore = (): void => {
      const isLoading = this.feedFacade.selectTabLoading(tabId)();

      if (isLoading) {
        setTimeout(checkAndRestore, ScrollManagementService.RESTORE_DELAY);
        return;
      }

      const scrollPosition = this.feedFacade.selectTabScrollPosition(tabId)();
      if (scrollPosition > 0) {
        setTimeout(() => {
          this.restoreScrollPosition(scrollPosition);
        }, ScrollManagementService.CONTENT_LOAD_DELAY);
      }
    };

    checkAndRestore();
  }

  scheduleScrollSave(tabId: CommunityTabType | undefined, delay = ScrollManagementService.DEFAULT_TIMER_DELAY): void {
    if (!tabId) return;

    this.clearExistingTimeout();
    this.scrollTimeout = window.setTimeout(() => {
      this.saveCurrentPosition(tabId);
    }, delay);
  }

  saveScrollPosition(tabId: CommunityTabType | undefined): void {
    if (tabId) {
      this.saveCurrentPosition(tabId);
    }
  }

  cleanup(): void {
    this.clearExistingTimeout();
  }

  scrollToPost(postId: string, behavior: ScrollBehavior = 'auto'): boolean {
    const postElement = document.querySelector(`[data-post-id="${postId}"]`);
    if (postElement) {
      postElement.scrollIntoView({
        behavior,
        block: 'start',
        inline: 'nearest',
      });
      return true;
    }
    return false;
  }

  scrollToPostWithRetries(postId: string, maxAttempts = ScrollManagementService.MAX_RESTORE_ATTEMPTS): void {
    let attempts = 0;

    const tryScroll = () => {
      if (attempts >= maxAttempts) {
        console.warn(`Could not scroll to post ${postId} after ${maxAttempts} attempts`);
        return;
      }

      if (this.scrollToPost(postId, 'auto')) {
        return;
      }

      attempts++;
      setTimeout(tryScroll, ScrollManagementService.RESTORE_DELAY);
    };

    tryScroll();
  }

  restoreScrollToTargetPost(tabId: CommunityTabType): void {
    const targetPostId = this.feedFacade.selectTargetPost(tabId)();
    if (targetPostId) {
      setTimeout(() => {
        this.scrollToPostWithRetries(targetPostId, ScrollManagementService.MAX_RESTORE_ATTEMPTS);
        this.feedFacade.clearTargetPost(tabId);
      }, ScrollManagementService.CONTENT_LOAD_DELAY);
    }
  }

  private attemptScrollRestoreWithRetries(position: number, attempt: number): void {
    if (attempt >= ScrollManagementService.MAX_RESTORE_ATTEMPTS) {
      this.scrollTo(position);
      return;
    }

    requestAnimationFrame(() => {
      const currentHeight = this.getDocumentHeight();

      if (this.canScrollToPosition(currentHeight, position)) {
        this.scrollTo(position);
      } else {
        setTimeout(() => {
          this.attemptScrollRestoreWithRetries(position, attempt + 1);
        }, ScrollManagementService.SCROLL_DELAY);
      }
    });
  }

  private canScrollToPosition(documentHeight: number, position: number): boolean {
    return documentHeight > position + ScrollManagementService.SCROLL_BUFFER;
  }

  private getDocumentHeight(): number {
    return Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
  }

  private scrollTo(position: number): void {
    window.scrollTo({
      top: position,
      behavior: 'auto',
    });
  }

  private saveCurrentPosition(tabId: CommunityTabType): void {
    const position = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = this.getDocumentHeight();
    this.feedFacade.setTabScrollPosition(tabId, position, documentHeight);
  }

  private clearExistingTimeout(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
  }
}
